/**
 * Accessible modal.
 *
 * Built on the native `<dialog>` element with `showModal()`, which gives us three
 * things the previous hand-rolled overlay promised and did not deliver:
 *
 *   1. Focus moves into the dialog on open, and is restored to the trigger on close.
 *   2. Focus is trapped — Tab cannot reach the inert page behind.
 *   3. Escape dismisses it.
 *
 * The old version declared `aria-modal="true"` while doing none of this, which is
 * worse than no ARIA at all: it made a false promise machine-readable. Screen
 * reader users were told the page behind was hidden, then allowed to tab into it.
 *
 * `<dialog>` also renders in the top layer, so it cannot be trapped beneath a
 * stacking context — a class of z-index bug we now cannot have.
 */

import { useEffect, useRef, type ReactNode } from 'react'
import { X } from 'lucide-react'

export const Modal = ({
  open,
  onClose,
  title,
  children,
  footer,
  labelledBy,
}: {
  open: boolean
  onClose: () => void
  title: string
  children: ReactNode
  footer?: ReactNode
  labelledBy?: string
}) => {
  const ref = useRef<HTMLDialogElement>(null)

  useEffect(() => {
    const dialog = ref.current
    if (!dialog) return
    if (open && !dialog.open) dialog.showModal()
    else if (!open && dialog.open) dialog.close()
  }, [open])

  // `close` fires for Escape as well as for programmatic close, so this single
  // listener covers both without a separate keydown handler.
  useEffect(() => {
    const dialog = ref.current
    if (!dialog) return
    const onNativeClose = () => onClose()
    dialog.addEventListener('close', onNativeClose)
    return () => dialog.removeEventListener('close', onNativeClose)
  }, [onClose])

  return (
    <dialog
      ref={ref}
      aria-labelledby={labelledBy ?? 'modal-title'}
      className="m-0 max-h-[92dvh] w-full max-w-2xl bg-transparent p-0 backdrop:bg-black/50 sm:m-auto"
      /* Clicking the backdrop closes. The check compares against the dialog box
         itself, so a click inside the panel never bubbles into a dismissal. */
      onClick={(event) => {
        if (event.target === ref.current) onClose()
      }}
    >
      <div className="flex max-h-[92dvh] w-full flex-col overflow-hidden rounded-t-box bg-base-100 text-base-content shadow-xl sm:rounded-box">
        <header className="flex items-center justify-between gap-3 border-b border-base-300 px-4 py-3">
          <h2 id={labelledBy ?? 'modal-title'} className="text-base font-semibold">
            {title}
          </h2>
          <button
            type="button"
            className="btn btn-ghost btn-sm btn-square"
            onClick={onClose}
            aria-label={`Close ${title.toLowerCase()}`}
          >
            <X size={18} />
          </button>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto p-4">{children}</div>

        {footer && (
          <footer className="flex flex-wrap justify-end gap-2 border-t border-base-300 p-3">
            {footer}
          </footer>
        )}
      </div>
    </dialog>
  )
}
